<template lang="pug">
  .make-an-entry-mood
    .make-an-entry-content
      MakeAnEntryVisibility(text="Always Private")
      section.section.pb-1.pt-2.mt-2
        WtTitle(h4 bold style="margin-bottom:5px") Mood
        WtLabel(
          style="color:#756E7A"
          text="How are you feeling?"
        )
    .slider-main-container.p-6
      BField
        BSlider.slider-element(
          :min=1
          :max=5
          v-model="mood"
          :tooltip="false"
          :ticks="false"
        )
          BSliderTick(v-for="tickData in ticksData" :value="tickData.label" :key="tickData.label")
            .tick-main-container
              .tick-number-container(:class="['tick-container-' + mood]")
                  .tick-number(:class="mood===tickData.label ? 'active-tick-number' : ''") {{ tickData.label }}
                  WtSvgIcon.bezier-svg-container(
                    v-if="mood === tickData.label"
                    icon="slider_number_selector/number_selector_wide"
                    width="100px"
                    height="50px"
                  )
              .tick-description(v-if="mood === tickData.label")
                WtSvgIcon(:icon="'icon_mood_0' + mood"  width="32px" height="32px")
                | {{ tickData.description }}
    .mood-text-container
      .mood-text-description What word best describes your mood?
      BDropdown(aria-role='list' v-model="moodText" scrollable position="is-top-right")
        template(#trigger='{ active }')
          BButton(:label="moodText || 'Choose'" :icon-right="active ? 'menu-up' : 'menu-down'")
        template(v-for="moodEntry in moodTextEntries")
          .dropdown-header.p-2.has-text-underlined.has-text-grey {{ moodEntry.header }}
          BDropdownItem(
            :key="entryName+index"
            aria-role='listitem'
            v-for="(entryName, index) in moodEntry.entries"
            :value="entryName"
          ) {{ entryName }}
    MakeAnEntryTip.ml-5(:tip-category="'mood'")
    .level.is-mobile.program-settings-form-controls
      .level-left
      .level-right
        .level-item
          WtButton(
            is-text
            color="white"
            style="text-decoration:none"
            size="medium"
            @click.native="reset"
            :disabled="disableReset"
          ) Reset
        .level-item
          WtButton(@click.native="saveEntry" :loading="savingMood" :disabled="disableSave") SAVE
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import MakeAnEntryFooter from "@@/Members/MakeAnEntry/MakeAnEntryFooter";
import MakeAnEntryVisibility from "@@/Members/MakeAnEntry/MakeAnEntryVisibility";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtLabel from "@@/WtLabel/WtLabel";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";
import MakeAnEntryTip from "@@/Members/MakeAnEntry/MakeAnEntryTip";

export default {
  name: "MakeAnEntryMood",

  components: {
    WtLabel,
    WtTitle,
    WtButton,
    MakeAnEntryFooter,
    WtSvgIcon,
    MakeAnEntryTip,
    MakeAnEntryVisibility
  },

  mixins: [dateUtilityMixin],

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    fetchAction: {
      type: Function,
      default: () => {},
      required: true
    },

    // "journal" or "discussion"
    type: {
      type: String,
      default: "journal"
    },

    entryItem: {
      type: Object,
      default: () => {},
      required: true
    },

    challengeId: {
      type: Number,
      default: null
    },

    entryDate: {
      type: String,
      default: ""
    },

    formData: {
      type: FormData,
      required: true,
      validator: value => {
        return value instanceof FormData;
      }
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      mood: 1,
      savingMood: false,
      ticksData: [
        {
          label: 1,
          description: "Terrible"
        },
        {
          label: 2,
          description: "Bad"
        },
        {
          label: 3,
          description: "Okay"
        },
        {
          label: 4,
          description: "Good"
        },
        {
          label: 5,
          description: "Awesome"
        }
      ],
      moodText: "",
      moodTextEntries: []
    };
  },

  computed: {
    ...mapGetters("program", ["coreProgramMeta"]),

    setFormData() {
      if (!this._.isEmpty(this.entryItem)) {
        // either it's changed or it might have been changed and changed back to what it was
        return (
          this.mood !== this.currentMetricValue ||
          this.moodText !== this.currentMetricText ||
          this.formData.has("mood") ||
          this.formData.has("moodText")
        );
      }
      return true;
    },

    currentMetricValue() {
      let metric = this._.find(this.entryItem.wellnessMetrics, {
        type: "mood"
      });
      return this._.get(metric, "value", 1);
    },

    currentMetricText() {
      let metric = this._.find(this.entryItem.wellnessMetrics, {
        type: "mood"
      });
      return this._.get(metric, "text", "");
    }
  },

  watch: {
    mood() {
      if (this.setFormData) {
        this.formData.set("mood", this.mood);
        this.formData.set("moodText", this.moodText);
        this.$emit("formDataUpdated", true);
      }
    },

    moodText() {
      if (this.setFormData) {
        this.formData.set("mood", this.mood);
        this.formData.set("moodText", this.moodText);
        this.$emit("formDataUpdated", true);
      }
    }
  },

  async mounted() {
    this.fetchMoodTextEntries();
    this.mood = this.currentMetricValue;
    this.moodText = this.currentMetricText;
  },

  methods: {
    ...mapActions("members", ["updateJournal", "storeJournal"]),

    saveEntry() {
      this.savingMood = true;
      this.saveEntryWrapper(this.storeJournal, this.updateJournal);
    },

    reset() {
      const item = "mood";
      if (this.formData.has(item)) {
        this.formData.delete(item);
      }
      if (this.formData.has("moodText")) {
        this.formData.delete("moodText");
      }
      this.mood = this.currentMetricValue;
      this.moodText = this.currentMetricText;
      this.$nextTick(() => {
        this.$emit("formReset", true);
      });
    },

    async fetchMoodTextEntries() {
      try {
        const response = await this.axios.get("journals/mood-entries");
        this.moodTextEntries = response.data;
      } catch (error) {
        this.error = true;
      }
    }
  }
};
</script>

<style lang="sass">
.make-an-entry-mood
  .wellness-visibility
    position: absolute
    right: 1rem
    top: .5rem
  .b-slider .b-slider-thumb
    height: 1.8rem
    width: 1.8rem
    border-radius: 50%
  .b-slider .b-slider-tick
    background-color: transparent
  .slider-main-container
    overflow: hidden
    height: 250px
  .slider-element
    position: relative
    width: 480px
    max-width: 480px
    padding-left: 70px
    padding-right: 70px
  .tick-main-container
    display: flex
    align-items: center
    justify-content: center
    flex-direction: column
    margin-top: 4px
  .tick-number
    text-align: center
    margin-top: 15px
    display: flex
    align-items: center
    justify-content: center
    font-size: 15px
  .tick-description
    background-color: $teal
    width: 160px
    padding: 2rem
    color: white
    text-align: center
    border-radius: 18px
    font-size: 16px
    height: 88px
    display: flex
    align-items: center
    justify-content: center
    flex-direction: column
    margin-top: 0
  .active-tick-number
    color: white
    position: absolute
    top: 0
  .tick-number-container
    display: flex
    position: relative
    justify-content: center
    align-items: center
  .mood-text-container
    display: flex
    align-items: center
  .mood-text-description
    margin-right: 20px
  .bezier-svg-container
    height: 50px
.program-settings-form-controls
  margin-top: 20px
</style>
